import React from 'react';
import {
  FormError,
  FormValues,
  useForm,
  useFormControl,
  useFormField,
} from '@tanium/react-form-context';

interface OnFormInputChangeEventArgs<TValue> {
  value: TValue;
}

interface OnFormInputChangeEventHandler<TValue> {
  (e: OnFormInputChangeEventArgs<TValue>): void;
}

type FormValue<TFields, K extends keyof TFields> = TFields[K];

export interface FormInputComponentProps<TValue> {
  /**
   * The `id` that should be assigned to the corresponding `input` element.
   */
  idForInput: string;
  /**
   * Any error(s) associated with the form field.
   */
  errors?: FormError[];
  /**
   * Whether there are any error(s) associated with the form field.
   */
  hasErrors: boolean;
  /**
   * The value corresponding to the form field.
   */
  value: TValue | undefined;
  /**
   * Callback to be invoked when the value is changed.
   */
  onChange: OnFormInputChangeEventHandler<TValue>;
}

export interface FormInputProps<TFields, K extends keyof TFields> {
  /**
   * Key to associate the input with the value / error objects from the enclosing Form. Defaults to
   * the `dataKey` of the nearest parent FormRow.
   */
  dataKey?: K;
  children: (props: FormInputComponentProps<FormValue<TFields, K>>) => React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function FormInput<TFields = any, K extends keyof TFields = any>({
  dataKey: propsDataKey,
  children,
}: FormInputProps<TFields, K>) {
  const { prefix, errors: formErrors, value } = useForm<TFields>();
  const { setValue } = useFormControl();
  const { dataKey: contextDataKey, errors: fieldErrors, labelId } = useFormField();

  const dataKey = propsDataKey || contextDataKey;

  const inputErrors = fieldErrors || (dataKey ? formErrors[dataKey] : undefined);
  const inputValue = dataKey ? value[dataKey as K] : undefined;
  const prefixedLabel = `${prefix}-${String(dataKey)}`;

  const idForInput = labelId || prefixedLabel;
  const hasErrors = Array.isArray(inputErrors) && !!inputErrors.length;

  return (
    <>
      {children({
        idForInput,
        hasErrors,
        value: inputValue,
        errors: inputErrors,
        onChange: ({ value: v }) => {
          if (dataKey) {
            setValue({ [dataKey]: v } as unknown as Partial<FormValues<TFields>>);
          }
        },
      })}
    </>
  );
}
